/**
 * NCALayer Service
 * 
 * Сервис для взаимодействия с NCALayer через WebSocket.
 * NCALayer - клиентское приложение для работы с ЭЦП РК.
 * 
 * Документация: https://pki.gov.kz/ncalayer/
 */

const NCALAYER_URL = 'wss://127.0.0.1:13579/'
const NCALAYER_MODULE = 'kz.gov.pki.knca.commonUtils'

class NCALayerService {
    constructor() {
        this.webSocket = null
        this.callback = null
        this.connectionPromise = null
        this.isConnected = false
    }

    /**
     * Подключение к NCALayer
     * @returns {Promise<boolean>} - успешность подключения
     */
    async connect() {
        if (this.isConnected && this.webSocket?.readyState === WebSocket.OPEN) {
            return true
        }

        if (this.connectionPromise) {
            return this.connectionPromise
        }

        this.connectionPromise = new Promise((resolve, reject) => {
            try {
                this.webSocket = new WebSocket(NCALAYER_URL)

                this.webSocket.onopen = () => {
                    console.log('NCALayer: Connection opened')
                    this.isConnected = true
                    resolve(true)
                }

                this.webSocket.onclose = (event) => {
                    console.log('NCALayer: Connection closed', event.code, event.reason)
                    this.isConnected = false
                    this.connectionPromise = null
                    if (!event.wasClean) {
                        reject(new Error('NCALayer не запущен или недоступен'))
                    }
                }

                this.webSocket.onerror = (error) => {
                    console.error('NCALayer: WebSocket error', error)
                    this.isConnected = false
                    this.connectionPromise = null
                    reject(new Error('Ошибка подключения к NCALayer'))
                }

                this.webSocket.onmessage = (event) => {
                    try {
                        const result = JSON.parse(event.data)
                        if (this.callback) {
                            this.callback(result)
                        }
                    } catch (e) {
                        console.error('NCALayer: Error parsing response', e)
                    }
                }

                // Таймаут подключения
                setTimeout(() => {
                    if (!this.isConnected) {
                        this.webSocket?.close()
                        reject(new Error('Таймаут подключения к NCALayer'))
                    }
                }, 5000)

            } catch (error) {
                reject(new Error('Не удалось создать подключение к NCALayer'))
            }
        })

        return this.connectionPromise
    }

    /**
     * Отправка сообщения в NCALayer
     * @param {Object} message - сообщение для отправки
     * @returns {Promise<Object>} - ответ от NCALayer
     */
    _send(message) {
        return new Promise((resolve, reject) => {
            if (!this.webSocket || this.webSocket.readyState !== WebSocket.OPEN) {
                reject(new Error('NCALayer не подключен'))
                return
            }

            const timeout = setTimeout(() => {
                this.callback = null
                reject(new Error('Таймаут ожидания ответа от NCALayer'))
            }, 120000) // 2 минуты на операцию (пользователь вводит пароль)

            this.callback = (result) => {
                clearTimeout(timeout)
                this.callback = null

                if (result.code === '500') {
                    reject(new Error(result.message || 'Ошибка NCALayer'))
                } else {
                    resolve(result)
                }
            }

            this.webSocket.send(JSON.stringify(message))
        })
    }

    /**
     * Проверка доступности NCALayer
     * @returns {Promise<boolean>}
     */
    async isNCALayerRunning() {
        try {
            await this.connect()
            return true
        } catch (error) {
            return false
        }
    }

    /**
     * Получение списка активных токенов (носителей ключей)
     * @returns {Promise<string[]>} - список доступных носителей
     */
    async getActiveTokens() {
        await this.connect()

        const result = await this._send({
            module: NCALAYER_MODULE,
            method: 'getActiveTokens'
        })

        // Добавляем PKCS12 (файловый носитель) в начало списка
        const tokens = result.responseObject || []
        return ['PKCS12', ...tokens]
    }

    /**
     * Получение информации о ключе
     * @param {string} storageName - имя носителя (PKCS12 или токен)
     * @returns {Promise<Object>} - информация о ключе
     */
    async getKeyInfo(storageName = 'PKCS12') {
        await this.connect()

        const result = await this._send({
            module: NCALAYER_MODULE,
            method: 'getKeyInfo',
            args: [storageName]
        })

        return result.responseObject
    }

    /**
     * Создание CMS подписи из Base64 данных
     * @param {string} storageName - имя носителя
     * @param {string} base64Data - данные для подписи в base64
     * @param {boolean} includeData - включать ли данные в подпись
     * @returns {Promise<string>} - CMS подпись в base64
     */
    async createCMSSignature(storageName, base64Data, includeData = true) {
        await this.connect()

        const result = await this._send({
            module: NCALAYER_MODULE,
            method: 'createCMSSignatureFromBase64',
            args: [storageName, 'SIGNATURE', base64Data, includeData]
        })

        return result.responseObject
    }

    /**
     * Подпись XML
     * @param {string} storageName - имя носителя
     * @param {string} xmlToSign - XML для подписи
     * @returns {Promise<string>} - подписанный XML
     */
    async signXml(storageName, xmlToSign) {
        await this.connect()

        const result = await this._send({
            module: NCALAYER_MODULE,
            method: 'signXml',
            args: [storageName, 'SIGNATURE', xmlToSign, '', '']
        })

        return result.responseObject
    }

    /**
     * Изменение языка интерфейса NCALayer
     * @param {string} locale - код языка (kk, ru, en)
     */
    async changeLocale(locale) {
        await this.connect()

        await this._send({
            module: NCALAYER_MODULE,
            method: 'changeLocale',
            args: [locale]
        })
    }

    /**
     * Закрытие соединения
     */
    disconnect() {
        if (this.webSocket) {
            this.webSocket.close()
            this.webSocket = null
            this.isConnected = false
            this.connectionPromise = null
        }
    }

    /**
     * Извлечение ИИН из subjectDn
     * @param {string} subjectDn - DN субъекта из сертификата
     * @returns {string|null} - ИИН или null
     */
    extractIINFromDN(subjectDn) {
        if (!subjectDn) return null

        // Паттерн для ИИН: SERIALNUMBER=IIN123456789012
        const match = subjectDn.match(/SERIALNUMBER=IIN(\d{12})/i)
        if (match) {
            return match[1]
        }

        // Альтернативный формат
        const match2 = subjectDn.match(/2\.5\.4\.5=IIN(\d{12})/i)
        if (match2) {
            return match2[1]
        }

        return null
    }
}

// Экспортируем singleton
export const ncaLayerService = new NCALayerService()
export default ncaLayerService
